import { useMemo } from "react";
import { fetchOrganizations, type Organization } from "../../../api/adminOrganizations";
import { fetchRoles, type Role } from "../../../api/adminRoles";
import { fetchUsers, type AdminUser } from "../../../api/adminUsers";
import { useLoadedData } from "../../../hooks/useLoadedData";

export interface AdminUsersData {
  users: AdminUser[];
  /** Every role, base and custom: people may hold any of them in each organization. */
  roles: Role[];
  organizations: Organization[];
}

const NOTHING_LOADED: AdminUsersData = { users: [], roles: [], organizations: [] };

async function loadAdminUsers(): Promise<AdminUsersData> {
  const [users, roles, organizations] = await Promise.all([fetchUsers(), fetchRoles(), fetchOrganizations()]);
  return { users, roles, organizations };
}

/** The users of the suite, with the roles and organizations needed to describe and edit them. */
export function useAdminUsers(): AdminUsersData & { loading: boolean; loadError: string | null; reload: () => void } {
  const { data, loading, loadError, reload } = useLoadedData(loadAdminUsers, "We couldn't load the user list.");
  const loaded = useMemo(() => data ?? NOTHING_LOADED, [data]);
  return { ...loaded, loading, loadError, reload };
}
